import {ApplicationRef, Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {VetClinicData} from '../models/vet-clinic-data.model';
import {ConnectionStatus} from '@capacitor/network';
import {switchMap, take} from 'rxjs/operators';
import {GenericNotification} from '../models/notification/generic-notification.model';
import {fromPromise} from 'rxjs/internal-compatibility';
import {Storage} from '@ionic/storage-angular';
import {GenericDgSearchResults} from '../models/datagrid-search-results/generic.dg-search-results.model';

/**
 * Globalny serwis zarządzania stanem aplikacji (State Management).
 * Przechowuje informacje o połączeniu z siecią, trwających operacjach
 * plikowych, powiadomieniach oraz stanie komponentów interfejsu (np. DataGrid).
 */
@Injectable({
  providedIn: 'root'
})
export class AppStateService {

  public isOffline = new BehaviorSubject<boolean>(true);
  public sseConnectionDropped = new BehaviorSubject<boolean>(false);
  public freshVetClinicDataConfigured = new BehaviorSubject<VetClinicData>(null);
  public mediaFilesStored = new BehaviorSubject<string>(null);

  private _editMode = new BehaviorSubject<'NONE' | 'EDIT' | 'CHANGED'>('NONE');
  private _screenRefreshRequest = new BehaviorSubject<boolean>(false);
  private _isBusySubject = new BehaviorSubject<boolean>(false);
  private _notificationsSubject = new BehaviorSubject<GenericNotification[]>(null);
  private _networkStatusSubject = new BehaviorSubject<ConnectionStatus>(null);
  private _mediaFilesCountSubject = new BehaviorSubject<Map<string, number>>(new Map());
  private _fileOperationInProgressSubject = new BehaviorSubject<Map<string, boolean>>(new Map());
  private _fileOperationInProgressNumberSubject = new BehaviorSubject<Map<string, number>>(new Map());
  private _dataGridLoadDataSubject = new BehaviorSubject<boolean>(false);
  private _onDataGridRefreshNeededSubject = new BehaviorSubject<GenericDgSearchResults>(null);

  constructor(
    private storage: Storage,
    private aref: ApplicationRef
  ){}

  /**
   * Zwraca strumień informujący o trwających operacjach blokujących w aplikacji (np. globalny loader).
   */
  get isBusySubject() {
    return this._isBusySubject.asObservable();
  }

  /**
   * Zwraca strumień informujący tabele DataGrid o konieczności odświeżenia danych.
   */
  get isOnDataGridRefreshNeededSubject() {
    return this._onDataGridRefreshNeededSubject.asObservable();
  }

  /**
   * Rozgłasza żądanie odświeżenia zawartości tabeli DataGrid.
   */
  updateOnDataGridRefreshNeededSubject(newValue: GenericDgSearchResults) {
    this._onDataGridRefreshNeededSubject.next(newValue);
  }

  /**
   * Zwraca strumień powiadomień.
   * Mechanizm Leniwego Ładowania (Lazy Loading): Jeśli pamięć RAM (Subject) jest pusta,
   * asynchronicznie dociąga powiadomienia z pamięci lokalnej (Storage) urządzenia.
   */
  get notificationsSubject() {
    if (this._notificationsSubject.getValue()) {
      return this._notificationsSubject.asObservable();
    } else {
      return fromPromise(this.getStorageObject<GenericNotification[]>('notifications').then((notificationsObj) => {
        return notificationsObj;
      })).pipe(take(1), switchMap(notifications => {
        if (notifications) {
          this.updateNotificationsSubject(notifications);
          return this._notificationsSubject;
        } else {
          this.updateNotificationsSubject([]);
          return this._notificationsSubject;
        }
      }));
    }
  }

  /**
   * Aktualizuje globalny tryb edycji w aplikacji.
   */
  updateEditMode(newValue: 'NONE' | 'EDIT' | 'CHANGED') {
    this._editMode.next(newValue);
  }

  /**
   * Zwraca strumień z żądaniami wymuszonego odświeżenia ekranu.
   */
  get screenRefreshRequest() {
    return this._screenRefreshRequest.asObservable();
  }

  /**
   * Zwraca strumień zawierający aktualny status połączenia z siecią (Online/Offline).
   */
  get networkStatusSubject() {
    return this._networkStatusSubject.asObservable();
  }

  /**
   * Zwraca strumień przechowujący mapę ilości przetworzonych plików multimedialnych.
   */
  get mediaFilesCountSubject() {
    return this._mediaFilesCountSubject.asObservable();
  }

  /**
   * Zwraca strumień informujący, które operacje plikowe są obecnie w toku.
   */
  get fileOperationInProgressSubject() {
    return this._fileOperationInProgressSubject.asObservable();
  }

  /**
   * Zwraca strumień z postępem numerycznym dla trwających operacji plikowych.
   */
  get fileOperationInProgressNumberSubject() {
    return this._fileOperationInProgressNumberSubject.asObservable();
  }

  /**
   * Zapisuje nowe powiadomienie do trwałej pamięci urządzenia (Ionic Storage)
   * oraz aktualizuje strumień, aby interfejs użytkownika natychmiast zareagował.
   * @param notification Obiekt powiadomienia do zapisania.
   */
  storeNotification(notification: GenericNotification) {
    if (notification) {
      this.getStorageObject<GenericNotification[]>('notifications').then(notificationsList => {
        let updatedNotificationsList: GenericNotification[] = [];
        if (notificationsList !== null) {
          updatedNotificationsList = notificationsList;
        }
        updatedNotificationsList.push(notification);
        this.setStorageObject('notifications', updatedNotificationsList).then(ok => {
          this.updateNotificationsSubject(updatedNotificationsList);
        });
      });
    }
  }

  /**
   * Rozgłasza żądanie odświeżenia bieżącego ekranu.
   */
  requestScreenRefresh() {
    this._screenRefreshRequest.next(true);
  }

  /**
   * Aktualizuje zawartość strumienia powiadomień.
   */
  updateNotificationsSubject(newValue: GenericNotification[]) {
    this._notificationsSubject.next(newValue);
  }

  /**
   * Ustawia lub zdejmuje globalną flagę zajętości aplikacji (Busy State).
   */
  updateIsBusySubject(newValue: boolean) {
    this._isBusySubject.next(newValue);
  }

  /**
   * Aktualizuje status fizycznego połączenia z siecią.
   */
  updateNetworkStatusSubject(newValue: ConnectionStatus) {
    this._networkStatusSubject.next(newValue);
  }

  /**
   * Aktualizuje licznik przetworzonych plików.
   */
  updateMediaFilesCount(newValue: Map<string, number>) {
    this._mediaFilesCountSubject.next(newValue);
  }

  /**
   * Oznacza operację plikową (identyfikowaną przez klucz Mapy) jako trwającą lub zakończoną.
   */
  updateFileOperationInProgress(newValue: Map<string, boolean>) {
    this._fileOperationInProgressSubject.next(newValue);
  }

  /**
   * Aktualizuje postęp numeryczny dla danej operacji plikowej.
   */
  updateFileOperationInProgressNumber(newValue: Map<string, number>) {
    this._fileOperationInProgressNumberSubject.next(newValue);
  }

  /**
   * Wrapper pobierający generyczny obiekt z pamięci lokalnej (Ionic Storage).
   */
  async getStorageObject<T>(key: string): Promise<T> {
    return this.storage.get(key);
  }

  /**
   * Wrapper zapisujący dowolny obiekt w pamięci lokalnej (Ionic Storage).
   */
  setStorageObject(key: string, value: any): Promise<any> {
    return this.storage.set(key, value);
  }

  /**
   * Włącza lub wyłącza animację ładowania dla komponentów DataGrid.
   */
  updateDataGridLoadSubject(isLoading: boolean) {
    console.log(isLoading)
    this._dataGridLoadDataSubject.next(isLoading);
  }

  /**
   * Zwraca strumień kontrolujący widoczność loaderów w DataGrid.
   */
  get dataGridLoadDataSubject() {
    return this._dataGridLoadDataSubject.asObservable();
  }

  /**
   * Wymusza natychmiastowe uruchomienie globalnego cyklu detekcji zmian (Change Detection)
   * w całym drzewie komponentów Angulara. Przydatne przy aktualizacjach asynchronicznych
   * wykraczających poza strefę NgZone.
   */
  detectChanges() {
    this.aref.tick();
  }
}
